/**
 * Every colour the projection and the phones share. Change a value here and both
 * apps/visuals and apps/join pick it up on their next build; no shader or stylesheet
 * holds its own copy.
 *
 * The universe colours are written raw: the shaders output them without any colour-space
 * conversion, so a hex value here is the hex value on screen.
 */
export const palette = {
  /**
   * The phone spark sprite's glow. Raw like the universe colours (it used to go through
   * three's sRGB conversion, which is why it is this deep amber).
   */
  spark: "#f86801",
  /**
   * The highlight: the sparks on the projection (`uTint`), the phone spark's solid core,
   * the highlighted word in the phone's copy and the last word of the projection's headline.
   */
  sparkCore: "#ffba0d",
  universe: {
    /** Tint of most dust and of the cool nebula clumps. */
    cool: "#d6ece8",
    /** Champagne: the warm stars and nebula clumps. */
    warm: "#f2d39e",
    /** Backdrop gradient: the dark base and its edges, also the page background on the phone. */
    deep: "#06292c",
    /** Backdrop gradient: the light falling in from the top. */
    light: "#4a9597",
  },
} as const;

/** `#rrggbb` as 0..1 channels, with no colour-space conversion. */
export function hexToRgb01(hex: string): [number, number, number] {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!match) throw new Error(`Not a #rrggbb colour: ${hex}`);
  const value = parseInt(match[1]!, 16);
  return [((value >> 16) & 0xff) / 255, ((value >> 8) & 0xff) / 255, (value & 0xff) / 255];
}

/** `#rrggbb` as 0..255 channels. */
export function hexToRgb255(hex: string): { r: number; g: number; b: number } {
  const [r, g, b] = hexToRgb01(hex);
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}
